import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Sun, Moon as MoonIcon, Sunrise, Coffee, Droplet, CheckCircle } from 'lucide-react';

const JourneyTimeline = () => {
  const timeline = [
    {
      time: '2:00 PM',
      icon: Sun,
      title: 'Light Lunch',
      description: 'Begin with a wholesome, light meal to prepare your system',
      color: 'from-yellow-400 to-orange-400',
    },
    {
      time: '6:00 PM',
      icon: Coffee,
      title: 'Herbal Cleanse Drink',
      description: 'Activate your digestive system with natural herbs',
      color: 'from-green-400 to-emerald-400',
    },
    {
      time: '8:00 PM',
      icon: Droplet,
      title: 'Gut Detox Elixir',
      description: 'Deep cleansing formula to flush out accumulated toxins',
      color: 'from-blue-400 to-cyan-400',
    },
    {
      time: '10:00 PM',
      icon: MoonIcon,
      title: 'Sleep Support Tonic',
      description: 'Calming blend to support overnight restoration',
      color: 'from-indigo-400 to-purple-400',
    },
    {
      time: '6:00 AM',
      icon: Sunrise,
      title: 'Morning Liver Flush',
      description: 'Gentle liver cleanse to eliminate overnight waste',
      color: 'from-pink-400 to-rose-400',
    },
    {
      time: '10:00 AM',
      icon: Droplet,
      title: 'Hydration Booster',
      description: 'Replenish and rehydrate your renewed system',
      color: 'from-cyan-400 to-blue-400',
    },
    {
      time: '11:00 AM',
      icon: CheckCircle,
      title: 'Detox Complete',
      description: 'Feel lighter, energized, and revitalized',
      color: 'from-green-400 to-emerald-500',
    },
  ];

  return (
    <div id="journey-timeline" className="py-20 px-4 bg-gradient-to-br from-purple-50 via-white to-accent">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-6 py-2 rounded-full mb-6">
            <Clock className="w-5 h-5" />
            <span className="font-semibold">16-Hour Journey</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Your Cleansing Timeline
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A carefully designed sequence that works with your body's natural rhythms
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-purple-300 to-green-400" />

          <div className="space-y-12">
            {timeline.map((step, index) => {
              const Icon = step.icon;
              const isLeft = index % 2 === 0;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative flex items-center ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-row`}
                >
                  {/* Content Card */}
                  <div className={`flex-1 ${isLeft ? 'md:pr-16' : 'md:pl-16'} pl-16 md:pl-0`}>
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-bold text-primary">
                              {step.time}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {step.title}
                          </h3>
                          <p className="text-gray-600">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Timeline Dot */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 + 0.2 }}
                    className={`absolute left-8 md:left-1/2 w-6 h-6 rounded-full bg-gradient-to-br ${step.color} border-4 border-white shadow-lg transform -translate-x-1/2`}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Reassurance Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="inline-block bg-gradient-to-r from-primary/10 via-purple-50 to-accent/50 rounded-3xl px-12 py-8">
            <p className="text-2xl font-semibold text-gray-800">
              💬 Gentle. Effective. 100% Natural.
            </p>
            <p className="text-gray-600 mt-2">
              Guided by certified therapists every step of the way
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default JourneyTimeline;
